import React, { useState, useRef, useEffect } from "react";
import { withStyles, makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import PublicAssignOneContractPharmacies from "./PublicAssignOneContractPharmacies";
import AsyncSelect from "react-select/async";
import debounce from "debounce-promise";
import _ from "lodash";

const CustomTextField = withStyles({
  root: {
    backgroundColor: "transparent",
    borderRadius: 8,
    "& label.Mui-focused": {
      color: "#3D71FF",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "green",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: "#ffffff",
      "& fieldset": {
        borderColor: "#DEE7EE",
      },
      "&:hover fieldset": {
        borderColor: "#DEE7EE",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#3D71FF",
      },
    },
  },
})(TextField);

const customSelectStyles = {
  container: (base) => ({
    ...base,
    margin: "16px 0 8px",
  }),
  control: (base) => ({
    ...base,
    minHeight: 56,
    borderColor: "#DEE7EE",
  }),
};

const PublicDesignationsForm = (props) => {
  const [completedUserDetails, setCompletedUserDetails] = useState(false);
  const [authorized, setAuthorized] = React.useState(false);
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [completedDesignations, setCompletedDesignations] = useState(false);
  const [userDetails, setUserDetails] = React.useState({
    firstName: null,
    lastName: null,
    email: null,
  });

  const loadOptions = (query) => fetchSearchTerm(query);
  const debouncedLoadOptions = debounce(loadOptions, 500, {
    leading: true,
  });

  const invalidEmailDomains = ["gmail", "outlook", "yahoo", "hotmail"];

  useEffect(() => {
    userDetailsCompleted();
  }, [userDetails]);

  const fetchSearchTerm = async (query) => {
    let url = new URL("/api/entities", window.location.origin);
    url.search = new URLSearchParams({ query: query, fetch_all: true }).toString();

    const results = await fetch(url)
      .then((response) => response.json())
      .then((data) => data)
      .catch((error) => {
        console.error("Error:", error);
      });

    return results;
  };

  const fetchEntityDetails = (url) => {
    const results = fetch(url, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content"),
      },
    })
      .then((response) => response.json())
      .then((data) => data);

    return results;
  };

  const handleSelectedEntities = (entitiesSelected) => {
    var urls = [];

    _.map(entitiesSelected, (selectedEntity) => {
      urls.push(`/api/entities/${selectedEntity.value}`);
    });

    Promise.all(urls.map((url) => fetchEntityDetails(url))).then((data) => {
      setSelectedEntities(data);
    });
  };

  const userDetailsCompleted = () => {
    if (!isInvalidEmail() && userDetails.email && userDetails.firstName && userDetails.lastName) {
      setCompletedUserDetails(true);
    } else {
      if (completedUserDetails) setCompletedUserDetails(false);
    }
  };

  const isInvalidEmail = () => {
    if (userDetails.email) {
      if (
        userDetails.email.includes("@") &&
        !_.map(invalidEmailDomains, (domain) => userDetails.email.includes(domain)).includes(true)
      ) {
        return false;
      } else {
        return true;
      }
    }
  };

  const enableSubmit = () => {
    return (
      completedUserDetails &&
      authorized &&
      selectedEntities &&
      selectedEntities.length &&
      completedDesignations
    );
  };

  const renderBillboard = () => {
    return (
      <div className="register__billboard">
        <div className="register__billboard__title">Designations</div>
        <div className="register__billboard__subtitle">All steps and fields are required *</div>
        <div className="register__billboard__step__container">
          <div
            className={
              completedUserDetails
                ? "register__billboard__step register__billboard__step--complete"
                : "register__billboard__step"
            }
          >
            1
          </div>
          <div className="register__billboard__step__details">
            <div className="register__billboard__step__details__title">Contact Details</div>
            <div className="register__billboard__step__details__subtitle">Provide your details</div>
          </div>
        </div>
        <div className="register__billboard__step__container">
          <div
            className={
              selectedEntities && selectedEntities.length
                ? "register__billboard__step register__billboard__step--complete"
                : "register__billboard__step"
            }
          >
            2
          </div>
          <div className="register__billboard__step__details">
            <div className="register__billboard__step__details__title">Covered Entities</div>
            <div className="register__billboard__step__details__subtitle">
              Select covered entity(s)
            </div>
          </div>
        </div>
        <div className="register__billboard__step__container">
          <div
            className={
              authorized
                ? "register__billboard__step register__billboard__step--complete"
                : "register__billboard__step"
            }
          >
            3
          </div>
          <div className="register__billboard__step__details">
            <div className="register__billboard__step__details__title">Authorization</div>
            <div className="register__billboard__step__details__subtitle">
              Confirm authorization
            </div>
          </div>
        </div>
        <div className="register__billboard__step__container">
          <div
            className={
              completedDesignations
                ? "register__billboard__step register__billboard__step--complete"
                : "register__billboard__step"
            }
          >
            4
          </div>
          <div className="register__billboard__step__details">
            <div className="register__billboard__step__details__title">Pharmacy Designations</div>
            <div className="register__billboard__step__details__subtitle">Make designations</div>
          </div>
        </div>
        <button
          className={enableSubmit() ? "btn" : "btn btn--disabled"}
          disabled={enableSubmit() ? false : true}
          type="submit"
        >
          Submit
        </button>
        <div className="register__billboard__footernote">
          Need Help? Contact Us
          <div className="register__billboard__footernote__contact">support@340besp.com</div>
        </div>
      </div>
    );
  };

  const renderEntitySelect = () => {
    return (
      <AsyncSelect
        components={{ DropdownIndicator: null, IndicatorSeparator: null }}
        openMenuOnFocus={false}
        openMenuOnClick={false}
        defaultOptions={false}
        isMulti
        name="entities[][entity_id]"
        placeholder={"Search Covered Entity *"}
        styles={customSelectStyles}
        isClearable={true}
        loadOptions={(query) => debouncedLoadOptions(query)}
        onChange={(values) => handleSelectedEntities(values)}
      />
    );
  };

  const renderEntityForm = () => {
    return (
      <div>
        <div className="register__form__requirements register__form__requirements--no-mt">
          <p>
            <span className="bold">Note: </span>
            Please select all covered entities for which your single contract pharmacy designation
            applies. You will be able to designate different contract pharmacies for each individual
            covered entity.
          </p>
        </div>
        <div id={"secondaryEntityForm"}>{renderEntitySelect()}</div>
      </div>
    );
  };

  return (
    <div className="register__wrapper register__wrapper--large">
      <form method="POST" action="/designations" id="designations_form">
        <div className="register__form__container register__form__container--large">
          <div className="register__step__title">1. Enter Contact Details</div>
          <div className="register__form register__form--large">
            <input
              type="hidden"
              name="authenticity_token"
              value={document.querySelector("[name=csrf-token]")?.content}
            />
            <div className="register__form__dual-row register__form__dual-row--pad-right">
              <CustomTextField
                label="First Name"
                margin="normal"
                variant="outlined"
                id="firstName"
                name="user[first_name]"
                autoFocus
                fullWidth
                required
                onChange={(event) => {
                  setUserDetails({
                    ...userDetails,
                    firstName: event.target.value,
                  });
                }}
              />
            </div>
            <div className="register__form__dual-row register__form__dual-row--pad-left">
              <CustomTextField
                label="Last Name"
                margin="normal"
                variant="outlined"
                id="lastName"
                name="user[last_name]"
                fullWidth
                required
                onChange={(event) => {
                  setUserDetails({
                    ...userDetails,
                    lastName: event.target.value,
                  });
                }}
              />
            </div>
            <div className="register__form__requirements">
              <p>
                <span className="bold">Note: </span>
                Please provide your
                <span className="bold"> Work Email Address.</span>
                <br />
                Designations are verified by authenticating the 340B covered entity domain with the
                email address provided.
              </p>
            </div>
            <CustomTextField
              label="Work Email Address"
              margin="normal"
              variant="outlined"
              id="email"
              name="user[email]"
              type="email"
              fullWidth
              required
              error={isInvalidEmail()}
              helperText={
                userDetails.email && isInvalidEmail()
                  ? "Email is not valid - Please provide a valid Work Email Address for this covered entity."
                  : null
              }
              onChange={(event) => {
                setUserDetails({
                  ...userDetails,
                  email: event.target.value,
                });
              }}
            />
          </div>
          <div className="register__step__title">2. Select Covered Entity(s)</div>
          <div className="register__form register__form--large">
            <div id={"secondaryEntityInputs"} />
            {renderEntityForm()}
          </div>
          <div className="register__step__title">3. Authorize Your Designation</div>
          <div className="register__form register__form--large">
            <p>
              Are you authorized on behalf of the selected covered entity(s) to designate a single
              contract pharmacy with 340B ESP™?
            </p>
            <FormControlLabel
              control={
                <Checkbox
                  checked={authorized}
                  onChange={() => {
                    setAuthorized(!authorized);
                  }}
                  color="primary"
                />
              }
              label="I am authorized"
            />
          </div>
          <div className="register__step__title">4. Designate a Single Contract Pharmacy</div>
          <div className="register__form register__form--large register__form--no-mb">
            <PublicAssignOneContractPharmacies
              entities={selectedEntities}
              setCompletedDesignations={setCompletedDesignations}
              manufacturerNames={props.manufacturerNames}
            />
          </div>
        </div>
        {renderBillboard()}
      </form>
    </div>
  );
};

export default PublicDesignationsForm;
